import React, { useCallback, useMemo, useState } from 'react';

import type { SelectValue } from 'antd/lib/select';
import { FormattedMessage } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

import { Button, Card, FlexSpacer, Select, T } from '@shared/components';
import {
  isMobileSel,
  organizationGuidSel,
} from '@shared/duck/selectors/general.selectors';
import { intl } from '@shared/localization';
import { getPreferredName } from '@shared/utils/preferredName';
import { openBasicReport } from '@utils';

import {
  availableYouthsForReportSel,
  closeSelectYouthModal,
  selectedReportSel,
} from '../../duck';
import styles from './SelectYouthModal.less';

const { Option } = Select;

const SelectYouthModalContent = () => {
  const dispatch = useDispatch();

  const [selectedPersonGuid, setSelectedPersonGuid] = useState<string>();

  const youths = useSelector(availableYouthsForReportSel);
  const organizationGuid = useSelector(organizationGuidSel);
  const report = useSelector(selectedReportSel);
  const isMobile = useSelector(isMobileSel);

  const handleViewReport = useCallback(() => {
    const { ReportURI } = report;
    openBasicReport(ReportURI, {
      organizationGuid,
      personGuid: selectedPersonGuid,
    });
    dispatch(closeSelectYouthModal());
  }, [organizationGuid, dispatch, report, selectedPersonGuid]);

  const handleCancel = () => dispatch(closeSelectYouthModal());

  const handleSetSelectedPersonGuid = (guid: SelectValue) => {
    setSelectedPersonGuid(guid as string);
  };

  const youthOptions = useMemo(
    () =>
      youths.map(youth => (
        <Option key={youth.personGuid} value={youth.personGuid || ''}>
          {getPreferredName(youth)}
        </Option>
      )),
    [youths],
  );

  return (
    <Card noPadding={!isMobile} shadow={isMobile} margin={isMobile}>
      {isMobile && (
        <T size="5" colored hr serif noMargin>
          <FormattedMessage
            id="reports.BasicReport.reportSelectYouth"
            values={{ reportName: report.ReportName }}
          />
        </T>
      )}
      <div className={styles.container}>
        <Select
          showSearch
          placeholder={intl.formatMessage({
            id: 'reports.BasicReport.selectYouth',
          })}
          onChange={handleSetSelectedPersonGuid}
          optionFilterProp="children"
        >
          {youthOptions}
        </Select>
        <div className={styles.buttons}>
          <Button
            onClick={handleCancel}
            type="primary"
            color="scouting-warm-gray"
            noBorder
            shadow
          >
            <FormattedMessage id="shared.cancel" />
          </Button>
          <FlexSpacer />
          <Button
            onClick={handleViewReport}
            type="primary"
            color="scouting-blue"
            noBorder
            shadow
            disabled={!selectedPersonGuid}
          >
            <FormattedMessage id="reports.BasicReport.viewReport" />
          </Button>
        </div>
      </div>
    </Card>
  );
};

export default SelectYouthModalContent;
